import {
  Box,
  FormControl,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";
import TextButton from "../../../../components/buttons/TextButton";
import { UserPlatformAccessRole } from "../../../../utils/constants";

const SetMyRoleModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { updateOwnAccessRole, trackEvent } = useAPI();
  const queryClient = useQueryClient();

  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedRole) {
      Toast.error("Please select a role");
      return;
    }

    setIsLoading(true);

    try {
      await updateOwnAccessRole(selectedRole);
      trackEvent(`User changed their own access role to ${selectedRole}`);
      Toast.success("Your role has been updated successfully");

      // Refresh the platform user data
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.user(),
      });

      handleClose();
      setSelectedRole("");
    } catch (error) {
      trackEvent(
        `User failed to change their own access role to ${selectedRole}`
      );
      if (error.response) {
        Toast.error(error.response.data);
      } else {
        Toast.error("Failed to update your role");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedRole("");
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="set-my-role-modal-title"
    >
      <Box
        sx={{
          width: 400,
          bgcolor: "background.paper",
          margin: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 24,
          borderRadius: "10px",
          overflow: "hidden",
          p: 3,
        }}
      >
        <Typography
          id="set-my-role-modal-title"
          variant="h6"
          component="h2"
          sx={{ mb: 2 }}
        >
          Set My Platform Role
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontSize={"14px"} sx={{ mb: 1 }}>
            Platform Role
          </Typography>
          <Select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            displayEmpty
            disabled={isLoading}
            sx={{
              "& .MuiSelect-select": {
                fontSize: "14px",
              },
            }}
          >
            <MenuItem value="" disabled>
              Select a role
            </MenuItem>
            {Object.values(UserPlatformAccessRole).map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <TextButton
            onClick={handleCloseModal}
            disabled={isLoading}
            color="inherit"
          >
            Cancel
          </TextButton>
          <TextButton
            onClick={handleSubmit}
            disabled={isLoading || !selectedRole}
            loading={isLoading}
            color="primary"
          >
            Update Role
          </TextButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default SetMyRoleModal;
